import { useEffect, useState } from 'react';

/**
 * Which channel lines are mid cadence-save (Dropping Cadence ticket): the
 * save re-plans the line server-side, so until it answers the queued
 * pills say "Updating…" instead of a fire time that is about to move.
 * Module-level store + pub-sub (the useEditorialCadence mechanism) — no
 * provider. Keys: `${brandId}:${platform}`; a universal save marks
 * `*:${platform}` (every brand that may inherit it).
 */

const pending = new Map<string, number>(); // key → in-flight saves
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((cb) => { try { cb(); } catch (e) { /* */ } });

const keyFor = (brandId: number | '*', platform: string) => `${brandId}:${platform}`;

/** Mark lines as updating until the returned promise settles. */
export async function whileCadenceUpdates<T>(
  brandId: number | '*', platforms: string[], work: () => Promise<T>,
): Promise<T> {
  const keys = platforms.map((p) => keyFor(brandId, p));
  keys.forEach((k) => pending.set(k, (pending.get(k) || 0) + 1));
  notify();
  try {
    return await work();
  } finally {
    keys.forEach((k) => {
      const n = (pending.get(k) || 1) - 1;
      if (n > 0) pending.set(k, n); else pending.delete(k);
    });
    notify();
  }
}

export function isCadenceUpdating(brandId: number, platform: string): boolean {
  return pending.has(keyFor(brandId, platform)) || pending.has(keyFor('*', platform));
}

/** True while a cadence save for this brand's channel line is in flight. */
export function useCadenceUpdating(brandId: number | undefined, platform: string | undefined): boolean {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    listeners.add(cb);
    return () => { listeners.delete(cb); };
  }, []);
  return !!brandId && !!platform && isCadenceUpdating(brandId, platform);
}
